import {
	createContext,
	useContext,
	useState,
	useEffect,
	ReactNode,
	useCallback,
	useMemo,
	useRef,
} from 'react'
import { useLocation } from 'react-router-dom'
import { Program as ProgramType } from '@/integrations/supabase/types/tables'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useTeamContext } from './TeamContext'
import { useAuth } from './AuthContext'
import { getAuthHeaders } from '@/services/api'
import { getApiBaseUrl } from '@/lib/api'
import { safeStorage } from '@/utils/storage'

// Actual row type
type ProgramRow = ProgramType['Row']

// Extended program type with access information
interface ProgramWithAccess extends ProgramRow {
	user_role?: string  // 'admin', 'owner', or 'member'
	access_level?: string  // 'admin', 'write', or 'read'
}

interface ProgramContextType {
	programId: string | null
	setProgramId: (id: string) => void
	programs: ProgramWithAccess[]
	loading: boolean
	error: Error | null
	currentProgram: ProgramWithAccess | null
	selectedProgram: ProgramWithAccess | null
	selectProgram: (program: ProgramWithAccess) => void
	createProgram: (name: string) => Promise<ProgramRow>
	fetchPrograms: (teamId: string) => Promise<void>
	currentProgramAccessLevel: string | null  // 'admin', 'write', or 'read'
	isInvitedProgram: boolean  // True if user was invited to the current program
}

export const ProgramContext = createContext<ProgramContextType | undefined>(undefined)

export const ProgramProvider = ({ children }: { children: ReactNode }) => {
	const location = useLocation()
	const queryClient = useQueryClient()
	const { isInitialized: authInitialized, session, loading: authLoading } = useAuth()
	const {
		teamId,
		isLoading: teamLoading,
		isInitialized: teamInitialized,
		setCurrentTeam,
	} = useTeamContext()
	const [programId, setProgramId] = useState<string | null>(() => {
		// Safely get from localStorage, returns null if unavailable
		return safeStorage.getItem('selectedProgramId')
	})
	const appliedProgramIdFromUrlRef = useRef<string | null>(null)

	const {
		data: programs = [],
		isLoading: programsLoading,
		error,
		refetch,
	} = useQuery<ProgramWithAccess[], Error>({
		queryKey: ['programs-all'],  // Changed to not depend on teamId
		queryFn: async () => {
			const headers = await getAuthHeaders()
			
			// If no auth token, this means session isn't ready yet
			if (!headers.Authorization) {
				console.log('[ProgramContext] No auth token available yet, skipping fetch')
				return []
			}
			
			const response = await fetch(
				`${getApiBaseUrl()}/api/programs`,  // No team_id parameter - fetch from all teams
				{ headers }
			)
			if (!response.ok) {
				const errorData = await response.json().catch(() => ({}))
				// If auth error, silently return empty array during initialization
				if (response.status === 401 || errorData.error === 'Authentication required') {
					console.log('[ProgramContext] Auth not ready yet, will retry')
					return []
				}
				console.error('[ProgramContext] Error fetching programs:', errorData)
				throw new Error(
					`Failed to fetch programs: ${response.status} ${
						response.statusText
					} - ${errorData.error || 'Unknown error'}`
				)
			}
			const data = await response.json()
			return data.programs || []
		},
		staleTime: 5 * 60 * 1000,
		gcTime: 10 * 60 * 1000,
		refetchOnWindowFocus: false,
		retry: 3,  // Increased retry count for auth timing issues
		retryDelay: 500,  // Wait 500ms between retries
		enabled: authInitialized && !!session && teamInitialized,  // Enable once auth and team are ready
	})

	const updateProgramId = useCallback(
		(id: string) => {
			if (id === programId) return

			// Use safe storage to persist the program ID
			safeStorage.setItem('selectedProgramId', id)
			setProgramId(id)

			// Find the program to get its team_id
			const selectedProgram = programs?.find(p => p.id === id)
			if (selectedProgram && selectedProgram.team_id && setCurrentTeam) {
				// Update the team context to match the selected program
				setCurrentTeam(selectedProgram.team_id)
			}

			queryClient.invalidateQueries({
				queryKey: ['programNotificationSettings', id],
			})
			queryClient.invalidateQueries({ queryKey: ['widgets', id] })
			queryClient.invalidateQueries({ queryKey: ['responses', id] })
			queryClient.invalidateQueries({ queryKey: ['dashboardData', id] })
		},
		[queryClient, programId, programs, setCurrentTeam]
	)

	useEffect(() => {
		// Prevent premature execution until the team ID is known. Otherwise the
		// `enabled` flag on the `useQuery` below toggles from false→true mid-life
		// which changes React Query's internal hook structure and trips React's
		// Rules-of-Hooks checker.
		if (teamLoading || programsLoading || !teamId) return

		// Use safe storage to get the stored program ID
		const storedId = safeStorage.getItem('selectedProgramId')
		let idToSet: string | null = null

		if (programs && programs.length > 0) {
			const isValidStoredId = storedId && programs.some((p) => p.id === storedId)

			if (isValidStoredId) {
				// Stored ID is valid, use it
				idToSet = storedId
			} else {
				// Stored ID is invalid or doesn't exist
				// Clear stale data from localStorage
				if (storedId) {
					console.warn(`Clearing stale program ID from localStorage: ${storedId}`)
					safeStorage.removeItem('selectedProgramId')
				}
				// Default to the first program
				idToSet = programs[0].id
			}
		} else {
			// No programs available
			idToSet = null
			// Clear localStorage since there are no programs
			if (storedId) {
				safeStorage.removeItem('selectedProgramId')
			}
		}

		// Core change: Always use updateProgramId if the resolved ID is different
		// from the current context programId, or if programId is null and we are setting an initial one.
		if (idToSet !== programId) {
			if (idToSet) {
				updateProgramId(idToSet)
			} else {
				// If idToSet is null (no programs or no valid selection)
				// and programId was previously something else.
				safeStorage.removeItem('selectedProgramId')
				// Directly set programId to null.
				setProgramId(null)
				// Manually invalidate queries that might need to clear out when no program is selected
				queryClient.invalidateQueries({ queryKey: ['widgets'] })
				queryClient.invalidateQueries({ queryKey: ['responses'] })
			}
		}
		// No 'else' needed: if idToSet is already programId, no change is necessary.
		// React Query's cache and stale-time mechanisms should handle data freshness for existing programId.
	}, [
		teamId,
		programs,
		programId,
		updateProgramId,
		teamLoading,
		programsLoading,
		queryClient,
		setProgramId,
	]) // Added queryClient and setProgramId to dependencies

	useEffect(() => {
		if (!programs || programs.length === 0) return

		const programIdFromUrl = new URLSearchParams(location.search).get('programId')
		if (!programIdFromUrl) {
			appliedProgramIdFromUrlRef.current = null
			return
		}

		// Apply URL-provided program only once per distinct value so users can
		// still switch projects manually after opening a deep link.
		if (appliedProgramIdFromUrlRef.current === programIdFromUrl) return
		appliedProgramIdFromUrlRef.current = programIdFromUrl

		const isAccessibleProgram = programs.some(
			(program) => program.id === programIdFromUrl
		)
		if (!isAccessibleProgram) return

		if (programIdFromUrl !== programId) {
			updateProgramId(programIdFromUrl)
		}
	}, [location.search, programs, programId, updateProgramId])

	const currentProgram = useMemo(
		() => programs?.find((program) => program.id === programId) || null,
		[programs, programId]
	)
	
	// Determine current program's access level and if it's an invited program
	const currentProgramAccessLevel = useMemo(
		() => currentProgram?.access_level || null,
		[currentProgram]
	)
	
	const isInvitedProgram = useMemo(
		() => Boolean(currentProgram?.access_level) && currentProgram?.access_level !== 'owner',
		[currentProgram]
	)

	const createProgram = useCallback(
		async (name: string): Promise<ProgramRow> => {
			if (!name.trim()) throw new Error('Program name is required.')

			// Use the current team context, or get default team if not set
			let currentTeamId = teamId

			if (!currentTeamId) {
				// If no team is selected, get the first available team
				const headers = await getAuthHeaders()
				const response = await fetch(`${getApiBaseUrl()}/api/team`, {
					headers,
				})
				
				if (!response.ok) {
					const errorData = await response.json().catch(() => ({}))
					throw new Error(errorData.error || 'Failed to fetch team data')
				}
				
				const teamData = await response.json()
				currentTeamId = teamData.team_id
				
				if (!currentTeamId) {
					throw new Error('No team available for creating a program.')
				}
				
				// Set this as the current team
				if (setCurrentTeam) {
					setCurrentTeam(currentTeamId)
				}
			}

			const headers = await getAuthHeaders()
			const response = await fetch(
				`${getApiBaseUrl()}/api/programs`,
				{
					method: 'POST',
					headers: {
						...headers,
						'Content-Type': 'application/json',
					},
					body: JSON.stringify({
						name: name.trim(),
					}),
				}
			)

			if (!response.ok) {
				const errorData = await response.json()
				throw new Error(errorData.error || 'Failed to create program')
			}
			const data = await response.json()
			// Invalidate all programs query since we fetch from all teams now
			await queryClient.invalidateQueries({
				queryKey: ['programs-all'],
			})
			if (data.program && data.program.id) {
				updateProgramId(data.program.id)
			}
			return data.program
		},
		[teamId, queryClient, updateProgramId, setCurrentTeam]
	)

	const fetchProgramsCallback = useCallback(
		async (currentTeamId: string) => {
			// Invalidate all programs since we fetch from all teams
			await queryClient.invalidateQueries({
				queryKey: ['programs-all'],
			})
			await queryClient.refetchQueries({
				queryKey: ['programs-all'],
			})
		},
		[queryClient]
	)

	const value = useMemo(
		() => ({
		programId,
		setProgramId: updateProgramId,
		programs: programs || [],
		loading: authLoading || teamLoading || programsLoading,
		error: error as Error | null,
			currentProgram,
			selectedProgram: currentProgram,
			selectProgram: (program: ProgramWithAccess) => updateProgramId(program.id),
			createProgram,
			fetchPrograms: fetchProgramsCallback,
			currentProgramAccessLevel,
			isInvitedProgram,
		}),
		[
			programId,
			updateProgramId,
			programs,
			authLoading,
			teamLoading,
			programsLoading,
			error,
			currentProgram,
			createProgram,
			fetchProgramsCallback,
			currentProgramAccessLevel,
			isInvitedProgram,
		]
	)

	return (
		<ProgramContext.Provider value={value}>{children}</ProgramContext.Provider>
	)
}

export const useProgramContext = () => {
	const context = useContext(ProgramContext)
	if (!context) {
		throw new Error('useProgramContext must be used within a ProgramProvider')
	}
	return context
}
